import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { RefreshCw, KeyRound, Plug, Copy, ArrowLeftRight, Loader2, CheckCircle, XCircle } from 'lucide-react';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

export default function SpecFiSync() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get('/admin/specfi/settings'),
        api.get('/admin/specfi/logs'),
      ]);
      setSettings(s.data);
      setLogs(l.data);
    } catch (e) {
      toast.error('Failed to load SpecFi sync settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { specfi_base_url, specfi_api_key, sync_members, sync_events, sync_donations, auto_sync_enabled } = settings;
      const res = await api.put('/admin/specfi/settings', {
        specfi_base_url, specfi_api_key, sync_members, sync_events, sync_donations, auto_sync_enabled,
      });
      setSettings(res.data);
      toast.success('SpecFi sync settings saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const generateKey = async () => {
    try {
      const res = await api.post('/admin/specfi/generate-key');
      setGeneratedKey(res.data.api_key);
      setSettings(prev => ({ ...prev, api_key: res.data.api_key }));
      toast.success('New API key generated');
    } catch (e) {
      toast.error('Failed to generate key');
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Copied to clipboard');
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/admin/specfi/sync-now');
      if (res.data.status === 'not_configured') {
        toast.error(res.data.detail);
      } else {
        const parts = Object.entries(res.data.results || {}).map(([r, v]) => `${r}: ${v.pushed} pushed`);
        toast.success(`Sync complete — ${parts.join(', ')}`);
      }
      load();
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post('/admin/specfi/test-connection');
      if (res.data.connected) {
        toast.success('Connected to SpecFi successfully');
      } else {
        toast.error(`Connection failed: ${res.data.detail || res.data.status_code}`);
      }
    } catch (e) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return <div className="flex items-center justify-center py-16" data-testid="specfi-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const maskedKey = settings.api_key ? `${settings.api_key.slice(0, 14)}...${settings.api_key.slice(-4)}` : null;

  return (
    <div className="space-y-6" data-testid="specfi-sync-panel">
      {/* Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Plug className="h-5 w-5" /> SpecFi Connection</CardTitle>
          <CardDescription>Connect to your SpecFi site to push members, events and donations to it.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>SpecFi Base URL</Label>
            <Input
              data-testid="specfi-base-url-input"
              placeholder="https://www.specfi.com"
              value={settings.specfi_base_url || ''}
              onChange={(e) => setSettings({ ...settings, specfi_base_url: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>SpecFi API Key (issued by SpecFi, used when pushing data to it)</Label>
            <Input
              data-testid="specfi-api-key-input"
              type="password"
              placeholder="Paste the sync API key from SpecFi"
              value={settings.specfi_api_key || ''}
              onChange={(e) => setSettings({ ...settings, specfi_api_key: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ['sync_members', 'Members'],
              ['sync_events', 'Events'],
              ['sync_donations', 'Donations'],
              ['auto_sync_enabled', 'Auto-sync (5 min)'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-2 border rounded-lg p-3">
                <Switch
                  data-testid={`specfi-toggle-${key}`}
                  checked={!!settings[key]}
                  onCheckedChange={(v) => setSettings({ ...settings, [key]: v })}
                />
                <span className="text-sm">{label}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveSettings} disabled={saving} data-testid="specfi-save-settings-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save Settings
            </Button>
            <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="specfi-test-connection-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}Test Connection
            </Button>
            <Button variant="secondary" onClick={syncNow} disabled={syncing} data-testid="specfi-sync-now-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-2" />}Sync Now
            </Button>
          </div>
          {settings.last_push_at && (
            <p className="text-xs text-muted-foreground" data-testid="specfi-last-push">Last push: {new Date(settings.last_push_at).toLocaleString()}</p>
          )}
        </CardContent>
      </Card>

      {/* Inbound API key */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Inbound API Key</CardTitle>
          <CardDescription>Give this key to SpecFi so it can pull from and push data to this platform.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {generatedKey ? (
            <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
              <code className="text-xs break-all flex-1" data-testid="specfi-generated-key">{generatedKey}</code>
              <Button size="icon" variant="ghost" onClick={() => copyKey(generatedKey)} data-testid="specfi-copy-key-btn"><Copy className="h-4 w-4" /></Button>
            </div>
          ) : maskedKey ? (
            <p className="text-sm text-muted-foreground" data-testid="specfi-masked-key">Current key: <code>{maskedKey}</code></p>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="specfi-no-key">No API key generated yet.</p>
          )}
          <Button variant="outline" onClick={generateKey} data-testid="specfi-generate-key-btn">
            <KeyRound className="h-4 w-4 mr-2" />{settings.api_key ? 'Regenerate Key' : 'Generate Key'}
          </Button>
          <div className="text-xs text-muted-foreground space-y-1 border rounded-lg p-3">
            <p className="font-medium text-foreground">Endpoints SpecFi can call (header: <code>X-Sync-Api-Key</code>):</p>
            <p><code>GET /api/sync/members|events|donations?since=ISO_DATE</code> — pull data from here</p>
            <p><code>POST /api/sync/members|events|donations</code> — push records here (body: {'{'}"records": [...]{'}'})</p>
            <p><code>POST /api/sync/webhook</code> — real-time single-record push (body: {'{'}"resource", "record"{'}'})</p>
          </div>
        </CardContent>
      </Card>

      {/* Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5" /> Sync Activity</CardTitle>
            <CardDescription>Recent sync operations</CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="specfi-refresh-logs-btn"><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="specfi-no-logs">No sync activity yet.</p>
          ) : (
            <div className="space-y-2" data-testid="specfi-logs-list">
              {logs.map((log) => (
                <div key={log.log_id} className="flex items-center gap-3 text-sm border-b pb-2 last:border-b-0">
                  {log.status === 'success' ? <CheckCircle className="h-4 w-4 text-green-500 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                  <Badge variant="outline" className="capitalize">{log.direction.replace(/_/g, ' ')}</Badge>
                  <span className="capitalize">{log.resource}</span>
                  <span className="text-muted-foreground">{log.count} record{log.count === 1 ? '' : 's'}</span>
                  <span className="text-xs text-muted-foreground ml-auto shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
